import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Filter } from 'lucide-react';
import ReactSelect, { MultiValue, StylesConfig } from 'react-select';
import { ALL_ORDER_STATUS, CHECKBOX_OPTIONS } from 'constants/Checkboxes';
import { FindMany, SelectOption } from 'contracts/Common';
import { OrderStatus } from 'contracts/Orders';
import {
  useCarriers,
  useCompanies,
  useDocks,
  useVehicleTypes,
  useWarehouses,
} from 'hooks';
import { DefaultSelectStyle } from 'components/Shared/Forms/CompactSelect/styles';
import { Fonts } from 'styles/constants';
import * as S from './styles';

export interface FindOrders extends FindMany {
  warehouseId?: unknown;
  warehouseIds?: number[];
  date?: string;
  fromDate?: string;
  toDate?: string;
  status?: OrderStatus[];
  orderId?: string;
  dockId?: number;
  vehicleTypeId?: number;
  vehiclePlate?: string;
  companyId?: number;
  carrierId?: number;
  createdByName?: string;
}

interface Props {
  currentFilter: FindOrders;
  onFilter?: (query: FindOrders) => void;
}

type FilterState = FindOrders & {
  orderId: string;
  vehiclePlate: string;
  createdByName: string;
  fromDate: string;
  toDate: string;
  warehouseIds: number[];
  status: OrderStatus[];
};

const fieldClass =
  'h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm outline-none focus:border-[#EE7C2C] focus:ring-1 focus:ring-[#EE7C2C]';
const labelClass = 'mb-1 block text-xs font-semibold text-gray-700';
const selectStyles: StylesConfig<SelectOption, boolean> = {
  ...(DefaultSelectStyle as StylesConfig<SelectOption, boolean>),
  control: (provided) => ({
    ...provided,
    fontFamily: Fonts.GilroyRegular,
    fontSize: '14px',
  }),
  input: (provided) => ({ ...provided, fontFamily: Fonts.GilroyRegular }),
  multiValueLabel: (provided) => ({
    ...provided,
    fontFamily: Fonts.GilroyRegular,
  }),
  option: (provided) => ({
    ...provided,
    fontFamily: Fonts.GilroyRegular,
    fontSize: '14px',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontFamily: Fonts.GilroyRegular,
  }),
  singleValue: (provided) => ({
    ...provided,
    fontFamily: Fonts.GilroyRegular,
  }),
};

const createFilterState = (filter: FindOrders): FilterState => ({
  ...filter,
  orderId: filter.orderId ?? '',
  vehiclePlate: filter.vehiclePlate ?? '',
  createdByName: filter.createdByName ?? '',
  fromDate: filter.fromDate ?? '',
  toDate: filter.toDate ?? '',
  warehouseIds:
    filter.warehouseIds ??
    (filter.warehouseId ? [Number(filter.warehouseId)] : []),
  status: Array.isArray(filter.status)
    ? filter.status
    : (ALL_ORDER_STATUS as OrderStatus[]),
});

const selectedOption = (
  options: SelectOption[],
  value?: number
): SelectOption | null =>
  value === undefined
    ? null
    : options.find((option) => Number(option.value) === value) ?? null;

const Filters: React.FC<Props> = ({ currentFilter, onFilter }) => {
  const [draft, setDraft] = useState<FilterState>(() => createFilterState(currentFilter));
  const [expanded, setExpanded] = useState(false);
  const [dateError, setDateError] = useState('');

  const { warehouseOptions, loadingWarehouses, fetchWarehouses } = useWarehouses();
  const { dockOptions, loadingDocks, fetchDocks } = useDocks();
  const { vehicleTypeOptions, loadingVehicleTypes, fetchVehicleTypes } = useVehicleTypes();
  const { companyOptions, loadingCompanies, fetchCompanies } = useCompanies();
  const { carrierOptions, loadingCarriers, fetchCarriers } = useCarriers();

  useEffect(() => setDraft(createFilterState(currentFilter)), [currentFilter]);

  useEffect(() => {
    fetchWarehouses();
    fetchDocks();
    fetchVehicleTypes();
    fetchCompanies();
    fetchCarriers();
  }, [fetchCarriers, fetchCompanies, fetchDocks, fetchVehicleTypes, fetchWarehouses]);

  const activeCount = useMemo(() => {
    const selectedStatuses = draft.status.filter((status) => status !== 'todos');
    const hasStatusFilter =
      selectedStatuses.length > 0 && selectedStatuses.length < CHECKBOX_OPTIONS.ORDER_STATUS.length - 1;

    return [
      draft.orderId,
      draft.fromDate,
      draft.toDate,
      draft.dockId,
      draft.vehicleTypeId,
      draft.vehiclePlate,
      draft.companyId,
      draft.carrierId,
      draft.createdByName,
    ].filter(Boolean).length +
      (draft.warehouseIds.length > 0 ? 1 : 0) +
      (hasStatusFilter ? 1 : 0);
  }, [draft]);

  const updateDraft = <Key extends keyof FilterState>(
    field: Key,
    value: FilterState[Key]
  ) => setDraft((current) => ({ ...current, [field]: value }));

  const toggleStatus = (status: OrderStatus) => {
    setDraft((current) => {
      if (status === 'todos') {
        return {
          ...current,
          status: current.status.includes('todos')
            ? (CHECKBOX_OPTIONS.ORDER_STATUS.filter(
                (option) => option.value !== 'todos'
              ).map((option) => option.value) as OrderStatus[])
            : (ALL_ORDER_STATUS as OrderStatus[]),
        };
      }

      const selected = current.status.filter((value) => value !== 'todos');
      const next = selected.includes(status)
        ? selected.filter((value) => value !== status)
        : [...selected, status];

      if (next.length === CHECKBOX_OPTIONS.ORDER_STATUS.length - 1) {
        next.push('todos');
      }

      return { ...current, status: next };
    });
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (draft.fromDate && draft.toDate && draft.toDate < draft.fromDate) {
      setDateError('A data final deve ser igual ou posterior à inicial.');
      return;
    }

    setDateError('');
    const selectedStatuses = draft.status.filter((status) => status !== 'todos');
    const status =
      selectedStatuses.length === 0 ||
      selectedStatuses.length === CHECKBOX_OPTIONS.ORDER_STATUS.length - 1
        ? undefined
        : selectedStatuses;

    onFilter?.({
      ...draft,
      orderId: draft.orderId.trim(),
      vehiclePlate: draft.vehiclePlate.trim().toUpperCase(),
      createdByName: draft.createdByName.trim(),
      warehouseId: undefined,
      status,
    });
  };

  const clear = useCallback(() => {
    const cleared = createFilterState({
      ...currentFilter,
      warehouseId: undefined,
      warehouseIds: [],
      date: undefined,
      fromDate: '',
      toDate: '',
      status: ALL_ORDER_STATUS as OrderStatus[],
      orderId: '',
      dockId: undefined,
      vehicleTypeId: undefined,
      vehiclePlate: '',
      companyId: undefined,
      carrierId: undefined,
      createdByName: '',
    });
    setDraft(cleared);
    setDateError('');
    onFilter?.(cleared);
  }, [currentFilter, onFilter]);

  return (
    <S.Container>
      <S.Toolbar>
        <S.ToggleButton
          type="button"
          aria-expanded={expanded}
          aria-controls="orders-filter-panel"
          onClick={() => setExpanded((current) => !current)}
        >
          <Filter size={14} aria-hidden="true" />
          Filtros
          {activeCount > 0 && <S.ActiveCount aria-label={`${activeCount} filtros ativos`}>{activeCount}</S.ActiveCount>}
        </S.ToggleButton>
      </S.Toolbar>

      {expanded && (
        <S.FilterPanel id="orders-filter-panel" onSubmit={submit}>
          <S.StatusGroup>
            <S.StatusLegend>Status</S.StatusLegend>
            {CHECKBOX_OPTIONS.ORDER_STATUS.map((option) => (
              <S.StatusOption key={option.value}>
                <input
                  type="checkbox"
                  checked={draft.status.includes(option.value as OrderStatus)}
                  onChange={() => toggleStatus(option.value as OrderStatus)}
                  style={{ accentColor: option.color }}
                />
                {option.label}
              </S.StatusOption>
            ))}
          </S.StatusGroup>

          <S.FieldsGrid>
            <S.Field>
              <label className={labelClass} htmlFor="orders-filter-id">Ordem</label>
              <input
                id="orders-filter-id"
                className={fieldClass}
                inputMode="numeric"
                value={draft.orderId}
                onChange={(event) => updateDraft('orderId', event.target.value.replace(/\D/g, ''))}
              />
            </S.Field>
            <S.Field>
              <label className={labelClass} htmlFor="orders-filter-from">Data inicial</label>
              <input id="orders-filter-from" type="date" className={fieldClass} value={draft.fromDate} onChange={(event) => updateDraft('fromDate', event.target.value)} />
            </S.Field>
            <S.Field>
              <label className={labelClass} htmlFor="orders-filter-to">Data final</label>
              <input id="orders-filter-to" type="date" className={fieldClass} value={draft.toDate} onChange={(event) => updateDraft('toDate', event.target.value)} />
            </S.Field>
            <S.Field>
              <label className={labelClass} htmlFor="orders-filter-dock">Doca Agendamento</label>
              <ReactSelect<SelectOption, false>
                inputId="orders-filter-dock"
                aria-label="Doca Agendamento"
                isClearable
                menuPortalTarget={document.body}
                options={dockOptions}
                value={selectedOption(dockOptions, draft.dockId)}
                isLoading={loadingDocks}
                styles={selectStyles as StylesConfig<SelectOption, false>}
                placeholder="Todas"
                onChange={(option) => updateDraft('dockId', option ? Number(option.value) : undefined)}
              />
            </S.Field>
            <S.Field>
              <label className={labelClass} htmlFor="orders-filter-vehicle-type">Veículo</label>
              <ReactSelect<SelectOption, false>
                inputId="orders-filter-vehicle-type"
                aria-label="Veículo"
                isClearable
                menuPortalTarget={document.body}
                options={vehicleTypeOptions}
                value={selectedOption(vehicleTypeOptions, draft.vehicleTypeId)}
                isLoading={loadingVehicleTypes}
                styles={selectStyles as StylesConfig<SelectOption, false>}
                placeholder="Todos"
                onChange={(option) => updateDraft('vehicleTypeId', option ? Number(option.value) : undefined)}
              />
            </S.Field>
            <S.Field>
              <label className={labelClass} htmlFor="orders-filter-plate">Placa</label>
              <input id="orders-filter-plate" className={`${fieldClass} uppercase`} value={draft.vehiclePlate} onChange={(event) => updateDraft('vehiclePlate', event.target.value)} />
            </S.Field>
            <S.Field>
              <label className={labelClass} htmlFor="orders-filter-company">Cliente</label>
              <ReactSelect<SelectOption, false>
                inputId="orders-filter-company"
                aria-label="Cliente"
                isClearable
                menuPortalTarget={document.body}
                options={companyOptions}
                value={selectedOption(companyOptions, draft.companyId)}
                isLoading={loadingCompanies}
                styles={selectStyles as StylesConfig<SelectOption, false>}
                placeholder="Todos"
                onChange={(option) => updateDraft('companyId', option ? Number(option.value) : undefined)}
              />
            </S.Field>
            <S.Field>
              <label className={labelClass} htmlFor="orders-filter-carrier">Transportadora</label>
              <ReactSelect<SelectOption, false>
                inputId="orders-filter-carrier"
                aria-label="Transportadora"
                isClearable
                menuPortalTarget={document.body}
                options={carrierOptions}
                value={selectedOption(carrierOptions, draft.carrierId)}
                isLoading={loadingCarriers}
                styles={selectStyles as StylesConfig<SelectOption, false>}
                placeholder="Todas"
                onChange={(option) => updateDraft('carrierId', option ? Number(option.value) : undefined)}
              />
            </S.Field>
            <S.Field>
              <label className={labelClass} htmlFor="orders-filter-creator">Criado por</label>
              <input id="orders-filter-creator" className={fieldClass} value={draft.createdByName} onChange={(event) => updateDraft('createdByName', event.target.value)} />
            </S.Field>
            <S.Field $wide>
              <label className={labelClass} htmlFor="orders-filter-warehouses">Unidade</label>
              <ReactSelect<SelectOption, true>
                inputId="orders-filter-warehouses"
                aria-label="Unidade"
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                menuPortalTarget={document.body}
                options={warehouseOptions}
                value={warehouseOptions.filter((option) => draft.warehouseIds.includes(Number(option.value)))}
                isLoading={loadingWarehouses}
                styles={selectStyles as StylesConfig<SelectOption, true>}
                placeholder="Todas as unidades"
                onChange={(options: MultiValue<SelectOption>) => updateDraft('warehouseIds', options.map((option) => Number(option.value)))}
              />
            </S.Field>
          </S.FieldsGrid>

          {dateError && <S.DateError role="alert">{dateError}</S.DateError>}
          <S.Actions>
            <button type="submit">Filtrar</button>
            <button type="button" onClick={clear}>Limpar filtros</button>
          </S.Actions>
        </S.FilterPanel>
      )}
    </S.Container>
  );
};

export default Filters;